import React from 'react';
import { useSelector } from 'react-redux';
import { NextPage } from 'next';
import Script from 'next/script';
import { Box } from '@mui/material';
import { getClientIp, getUserPremiumStatus } from '@surfline/web-common';

import {
  fetchNearbySubregions,
  fetchOverviewData,
  fetchRegionalArticles,
  setSubregionLoading,
} from 'actions/subregion';
import { fetchSwellEvents } from 'actions/swellEvents';
import { setUserPermissions } from 'actions/user';
import { JW_PLAYER_URL } from 'common/constants';
import { SL_PREMIUM_FORECASTS } from 'common/permissions';
import { PremiumFCSTUpgradePill } from 'components/CTAs';
import PremiumAnalysis from 'containers/PremiumAnalysis';
import useJWPlayer from 'hooks/useJWPlayer';
import { getSubregionOverviewData } from 'selectors/subregion';
import { getOrCreateStore, ReduxInitialProps } from 'stores';
import type { SubregionOverviewData } from 'types/subregion';
import type { WavetrakPageContext } from 'types/wavetrak';
import getSpotPageRedirectURL from 'utils/getSpotPageRedirectURL';
import getCloudflareCountryCode from 'utils/getCloudflareCountryCode';

import styles from './SubregionForecastPage.module.scss';

const SubregionForecastPage: NextPage<
  {},
  ReduxInitialProps & { isSubregionForecastPage: true }
> = () => {
  const overviewData = useSelector(getSubregionOverviewData);
  const isPremium = useSelector(getUserPremiumStatus);
  const { jwPlayerLoaded, setJwPlayerLoaded } = useJWPlayer();

  return (
    <div data-testid="subregion-forecast-page">
      {overviewData && (
        <div>
          <Box className="sl-subregion-forecast-page">
            <Script
              id="jw-player"
              strategy="afterInteractive"
              src={JW_PLAYER_URL}
              onLoad={() => setJwPlayerLoaded(true)}
            />
            <Box className={styles.regionalAnalysisContainer}>
              <PremiumAnalysis jwPlayerLoaded={jwPlayerLoaded} />
            </Box>
          </Box>
        </div>
      )}
      {!isPremium && <PremiumFCSTUpgradePill />}
    </div>
  );
};

SubregionForecastPage.getInitialProps = async (context: WavetrakPageContext) => {
  const store = getOrCreateStore();
  const { dispatch } = store;
  const { res, req, query } = context;

  const { subregionSlug, subregionId } = query as { subregionSlug: string; subregionId: string };

  const cookies = req?.cookies || {};

  const clientIp = getClientIp(context.req);

  const isServer = !!req;

  const countryCode = getCloudflareCountryCode(req);

  await dispatch(setSubregionLoading(true));

  await Promise.all([
    dispatch(
      fetchOverviewData(
        subregionId,
        cookies,
        clientIp,
        true,
        (overview: SubregionOverviewData) =>
          dispatch(
            setUserPermissions({
              requestedPermissions: [SL_PREMIUM_FORECASTS],
              permissions: overview?.permissions,
            }),
          ),
        countryCode,
      ),
    ),
    dispatch(fetchNearbySubregions(subregionId, cookies)),
    dispatch(fetchRegionalArticles(subregionId, cookies, isServer)),
    dispatch(fetchSwellEvents(subregionId, 'subregion')),
  ]);

  dispatch(setSubregionLoading(false));

  const { subregion } = store.getState();
  if (subregion) {
    const redirectUrl = getSpotPageRedirectURL({
      data: subregion?.overview?.overview?.data,
      feedArticles: subregion?.feed?.articles,
      primarySpotId: subregion?.primarySpotId,
    });

    if (res?.locals && redirectUrl) {
      res.locals.redirectUrlFromPremiumAnalysis = redirectUrl;
    }
  }

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      subregionSlug,
      subregionId,
    });
    return {
      enableCssBaseline: true,
      enableTheme: true,
      isSubregionForecastPage: true,
      librariesToLoad: {
        jwplayer: true,
      },
      ssrReduxState: store.getState(),
    };
  }

  return {
    enableCssBaseline: true,
    enableTheme: true,
    isSubregionForecastPage: true,
    librariesToLoad: {
      jwplayer: true,
    },
  };
};

export default SubregionForecastPage;
